import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
  sizes: string[];
  isNew?: boolean;
  badge?: string;
}

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      to={`/product/${product.id}`}
      className="group block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative overflow-hidden rounded-lg bg-[#1A1A1A] border border-white/10 hover:border-white/30 transition-all duration-300">
        {/* Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
          />
          
          {/* Badges */}
          {product.badge && (
            <div className="absolute top-4 left-4 bg-white text-black px-3 py-1 rounded-full text-xs font-black tracking-wide">
              {product.badge}
            </div>
          )}
          {product.isNew && (
            <div className="absolute top-4 right-4 bg-white text-black px-3 py-1 rounded-full text-xs font-black tracking-wide">
              NEW
            </div>
          )}

          {/* Quick View Overlay */}
          <div
            className={`absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <button className="px-6 py-3 bg-white text-black rounded-lg font-bold text-sm hover:scale-105 transition-all whitespace-nowrap">
              QUICK VIEW
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-5">
          <div className="mb-2">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-gray-500 mb-1">
              {product.category}
            </p>
            <h3 className="text-base font-bold text-white group-hover:text-gray-300 transition-colors line-clamp-2">
              {product.name}
            </h3>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xl font-black text-white">${product.price}</p>
            
            {/* Sizes Available */}
            <div className="flex items-center gap-1">
              {product.sizes.slice(0, 3).map((size) => (
                <span
                  key={size}
                  className="text-xs font-bold text-gray-500 px-2 py-1 bg-white/5 rounded"
                >
                  {size}
                </span>
              ))}
              {product.sizes.length > 3 && (
                <span className="text-xs font-bold text-gray-500">
                  +{product.sizes.length - 3}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}